import type {KeyTextField} from '@prismicio/types';
import type {JobsFamilySliceSliceDefaultItem, JobsFamilySliceSliceDefaultPrimary} from '@prismicio-types';
import domTypeGuards from '~/composables/domTypeGuards';

const {hasProperty} = domTypeGuards();

type Job = JobsFamilySliceSliceDefaultItem;
export type JobsByJobFamily = {
    unknownFamily: Job[];
    family1: Job[];
    family2: Job[];
    family3: Job[];
    family4: Job[];
    family5: Job[];
};

const MAX_JOBS_PER_FAMILY = 8;

const JOB_FAMILIES = [
    {jobsKey: 'family1', labelKey: 'jobFamily1', ordinal: 'first'},
    {jobsKey: 'family2', labelKey: 'jobFamily2', ordinal: 'second'},
    {jobsKey: 'family3', labelKey: 'jobFamily3', ordinal: 'third'},
    {jobsKey: 'family4', labelKey: 'jobFamily4', ordinal: 'fourth'},
    {jobsKey: 'family5', labelKey: 'jobFamily5', ordinal: 'fifth'},
] as const;

type JobFamilyPrimaryLabels = Pick<JobsFamilySliceSliceDefaultPrimary, 'jobFamily1' | 'jobFamily2' | 'jobFamily3' | 'jobFamily4' | 'jobFamily5'>;

export type JobsFamilyPreviewValidation = {
    enabled: boolean;
    toast: (message: string) => void;
    hasLessThanTwoLabels: (labels: KeyTextField[]) => boolean;
    primary: JobFamilyPrimaryLabels;
    items: Job[];
    jobsByJobFamily: JobsByJobFamily;
};

export function getJobsByJobFamily(jobs: Job[]): JobsByJobFamily {
    return jobs.reduce(
        (jobsByJobFamily, job) => {
            const jobFamily = job.jobFamily;
            if (
                jobFamily === null ||
                (jobFamily !== 'family1' && jobFamily !== 'family2' && jobFamily !== 'family3' && jobFamily !== 'family4' && jobFamily !== 'family5')
            ) {
                jobsByJobFamily.unknownFamily.push(job);
                return jobsByJobFamily;
            }
            jobsByJobFamily[jobFamily].push(job);

            return jobsByJobFamily;
        },
        {
            unknownFamily: <Job[]>[],
            family1: <Job[]>[],
            family2: <Job[]>[],
            family3: <Job[]>[],
            family4: <Job[]>[],
            family5: <Job[]>[],
        },
    );
}

export function isFamilyEmpty(familyLabel: string | null, jobs: Job[]) {
    return familyLabel !== null && jobs.length === 0;
}

export function getJobFamilyNamesNotEmpty(primary: unknown) {
    return ['jobFamily1', 'jobFamily2', 'jobFamily3', 'jobFamily4', 'jobFamily5'].reduce(
        (acc, family) => {
            if (hasProperty(primary, family)) {
                const familyName = primary[family];
                if (typeof familyName === 'string') {
                    acc.push({name: familyName});
                }
            }
            return acc;
        },
        <{name: string}[]>[],
    );
}

export function validateJobsFamilyPreview({enabled, toast, hasLessThanTwoLabels, primary, items, jobsByJobFamily}: JobsFamilyPreviewValidation) {
    if (!enabled) {
        return;
    }

    for (const family of JOB_FAMILIES) {
        if (jobsByJobFamily[family.jobsKey].length > MAX_JOBS_PER_FAMILY) {
            toast(`The number of jobs in the ${family.ordinal} family is greater than ${MAX_JOBS_PER_FAMILY}`);
        }
    }

    if (JOB_FAMILIES.some((family) => isFamilyEmpty(primary[family.labelKey], jobsByJobFamily[family.jobsKey]))) {
        toast('A job family is defined with no jobs assigned to it');
    }

    if (hasLessThanTwoLabels(JOB_FAMILIES.map((family) => primary[family.labelKey]))) {
        toast('There are less than two job families defined');
    }

    if (items.length === 0) {
        toast('The slice jobsFamily has no jobs');
    }
}
